/**
 * Centralized Event Bus for Domain Events
 *
 * Usa EventEmitter3 per gestione eventi ad alte prestazioni.
 * Singleton pattern per garantire una singola istanza nell'applicazione.
 *
 * Usage:
 * ```typescript
 * import { eventBus } from './eventBus';
 *
 * // Emit event
 * eventBus.emitEvent({
 *   type: 'lead:created',
 *   payload: { leadId: '123', ... }
 * });
 *
 * // Listen to event
 * eventBus.onEvent('lead:created', (payload) => {
 *   console.log('Lead created:', payload.leadId);
 * });
 * ```
 */
import EventEmitter from 'eventemitter3';
import type { DomainEvent } from './types';

class DomainEventBus extends EventEmitter<DomainEvent['type']> {
  private static instance: DomainEventBus;

  private constructor() {
    super();

    // Debug logging is handled in emitEvent method
    // EventEmitter3 doesn't have onAny - we log when emitting
  }

  /**
   * Singleton pattern - ensure single EventBus instance
   */
  static getInstance(): DomainEventBus {
    if (!DomainEventBus.instance) {
      DomainEventBus.instance = new DomainEventBus();
    }
    return DomainEventBus.instance;
  }

  /**
   * Type-safe emit method
   *
   * @param event - Domain event with type and payload
   * @returns true if the event had listeners, false otherwise
   */
  emitEvent<T extends DomainEvent>(event: T): boolean {
    return this.emit(event.type, event.payload);
  }

  /**
   * Type-safe listener registration
   *
   * @param event - Event type to listen to
   * @param handler - Handler function that receives the payload
   * @returns this (for chaining)
   */
  onEvent<T extends DomainEvent['type']>(
    event: T,
    handler: (
      payload: Extract<DomainEvent, { type: T }>['payload']
    ) => void | Promise<void>
  ): this {
    return this.on(event, handler);
  }

  /**
   * Remove listener
   *
   * @param event - Event type
   * @param handler - Handler function to remove
   * @returns this (for chaining)
   */
  offEvent<T extends DomainEvent['type']>(
    event: T,
    handler: (
      payload: Extract<DomainEvent, { type: T }>['payload']
    ) => void | Promise<void>
  ): this {
    return this.off(event, handler);
  }

  /**
   * Get listener count for debugging
   *
   * @param event - Optional event type. If not provided, returns total count.
   * @returns Number of listeners
   */
  getListenerCount(event?: DomainEvent['type']): number {
    if (event) {
      return this.listenerCount(event);
    }
    // Sum all listeners across all events
    return this.eventNames().reduce(
      (acc, name) => acc + this.listenerCount(name),
      0
    );
  }

  /**
   * Get all registered event names
   * Useful for debugging and monitoring
   */
  getRegisteredEvents(): DomainEvent['type'][] {
    return this.eventNames() as DomainEvent['type'][];
  }

  /**
   * Check if a specific event has any listeners
   */
  hasListeners(event: DomainEvent['type']): boolean {
    return this.listenerCount(event) > 0;
  }
}

// Export singleton instance
export const eventBus = DomainEventBus.getInstance();

// Export class for testing (can create new instances in tests)
export { DomainEventBus };
